import { atom, useSetAtom } from 'jotai';
import { atomWithDefault, RESET } from 'jotai/utils';
import { useCallback } from 'react';
import { getInitialClientInvestmentRows } from '../../pages/CompanyProfiles/CapTable/captable-utils';
import {
  IInvestmentGridRow,
  IOtherInvestmentsGridRow,
} from '../../pages/CompanyProfiles/CapTable/InvestmentGridDefs';
import { sortShareClasses } from '../../pages/CompanyProfiles/CapTable/investment-utils';
import { generateInitialInvestmentRows } from '../../pages/CompanyProfiles/CapTable/OtherInvestmentsDefs';
import { selectedCompanyIdProfileAtom } from '../../pages/CompanyProfiles/state/UIStateJ';
import { getForesightStore } from '../../util/jotai-store';
import { ShareClass } from '../../view-models/captable.view-model';
import { selectableFundsAtom } from './AppConfigStateJ';
import { metricsFundDataByFundIdAtomJ } from './CompanyMetricsStateJ';
import { getCompanySyncIfResolved } from './CompanyStateJ';

/**
 * Share classes for the cap-table form. Resets to `[]` via the `useResetCapTableFormStateJ` hook.
 */
export const shareClassesStateJ = atom<ShareClass[]>([]);

/**
 * Current step (zero-indexed) for the multi-step cap-table form.
 *
 * Co-located with the form atoms because the reset hook needs it. Consumed by
 * `StepperFormButtons` via the `stepState` prop after `StepperFormButtons` was
 * migrated to accept `PrimitiveAtom<number>` in PR 9e.
 */
export const currentStepCapTableStateJ = atom<number>(0);

/**
 * Sorted derivation of `shareClassesStateJ`.
 */
export const sortedShareClassesStateJ = atom((get) => sortShareClasses(get(shareClassesStateJ)));

/**
 * Returns a fresh dummy row used as the trailing "add row" placeholder in the
 * client investments grid.
 */
export function getDummyClientInvestmentRow(): IInvestmentGridRow {
  return { fundId: null };
}

/**
 * Initial client-investment rows for the cap-table form.
 *
 * `shareClassesStateJ` is the only reactive dependency — `companyId`, `funds`,
 * and `metricsFundDataByFundIdAtomJ` are read via non-reactive store peeks
 * because the form is initialised once per company and resets on unmount.
 * Re-deriving when the company id changes mid-form would clobber unsaved input.
 */
export const clientInvestmentsStateJ = atomWithDefault<IInvestmentGridRow[]>((get) => {
  const shareClasses = get(shareClassesStateJ);
  const store = getForesightStore();
  const funds = store.get(selectableFundsAtom);
  const companyId = store.get(selectedCompanyIdProfileAtom);
  const company = getCompanySyncIfResolved(companyId);
  if (!company) {
    return [getDummyClientInvestmentRow()];
  }
  const companyFundIds = store.get(metricsFundDataByFundIdAtomJ(companyId))?.keys();

  const rows = getInitialClientInvestmentRows({
    company,
    funds,
    companyFundIds,
    shareClasses,
  });

  const dummyRow = getDummyClientInvestmentRow();
  return [...rows, { ...dummyRow }];
});

/**
 * Initial "other investments" rows derived from the current share classes.
 * Resets to the derived default via `useResetCapTableFormStateJ`.
 */
export const otherInvestmentsStateJ = atomWithDefault<IOtherInvestmentsGridRow[]>((get) =>
  generateInitialInvestmentRows(get(shareClassesStateJ))
);

/**
 * Resets the cap-table form atoms to their defaults.
 *
 * `shareClassesStateJ` and `currentStepCapTableStateJ` are plain `atom`s, so
 * we set them back to their initial values directly. `clientInvestmentsStateJ`
 * and `otherInvestmentsStateJ` are `atomWithDefault`, so we send the `RESET`
 * symbol from `jotai/utils` to clear any override and restore the derived
 * default.
 */
export function useResetCapTableFormStateJ() {
  const setShareClasses = useSetAtom(shareClassesStateJ);
  const setCurrentStep = useSetAtom(currentStepCapTableStateJ);
  const setClientInvestments = useSetAtom(clientInvestmentsStateJ);
  const setOtherInvestments = useSetAtom(otherInvestmentsStateJ);

  return useCallback(() => {
    setCurrentStep(0);
    setShareClasses([]);
    setClientInvestments(RESET);
    setOtherInvestments(RESET);
  }, [setCurrentStep, setShareClasses, setClientInvestments, setOtherInvestments]);
}
